import { useEffect, useRef, useState } from 'react'
import { hubLangFor } from '../lib/i18n'
import { STARS } from '../lib/hubCopy'
import { nudgeStars, onStarsEarned, primeStarPulse } from '../lib/starPulse'
// For its side effect: stars.ts registers loadMyStars as the pulse's refresher,
// and this component must work on a screen that imports nothing else from it.
import '../lib/stars'
import { useStarOne } from '../lib/terms'

/**
 * The "+25 stars" pill. REC-57, priority 2 (David, 24 Sep). Mounted once, in
 * AppShell, so it works on every screen that can earn — the hub, Wiki One,
 * Star One.
 *
 * ── A STATUS, NOT A TOAST WITH BUTTONS ──────────────────────────────────────
 *
 * `role="status"` is rendered ALWAYS and only its content changes, because a
 * live region that appears at the same moment as its text is one most screen
 * readers never announce. The visible pill is `aria-hidden` and the sentence
 * beside it is what is read — "+25 ★" read aloud is "plus twenty-five black
 * star". `pointer-events: none`: it must never sit on top of the thing
 * somebody was about to tap, and there is nothing to press on it.
 *
 * ── TWO EARNS IN A ROW ADD UP ───────────────────────────────────────────────
 *
 * A second rise while the pill is showing adds to the number and restarts the
 * clock, rather than replacing "+20" with "+5" and losing the first.
 *
 * ── COMING BACK TO THE TAB LOOKS AGAIN ──────────────────────────────────────
 *
 * A recognition matures fifteen minutes after it is given (063), which is
 * nobody's action — so returning to the tab nudges a read, and that is where
 * "+100 stars" from a colleague appears. Only a VISIBLE tab: a background tab
 * asking every time the window blurs would be reads nobody sees.
 */
const SHOW_MS = 4500

export function StarPulse({ locale = 'en-CA' }: { locale?: string }) {
  const t = STARS[hubLangFor(locale)]
  const [gained, setGained] = useState(0)
  // 165: no "+25 stars" for somebody who has not joined Star One — it is star
  // activity, and the agreement comes first. The live region stays mounted.
  const joined = useStarOne().status === 'agreed'
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    primeStarPulse()
    const off = onStarsEarned((n) => {
      setGained((g) => g + n)
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => setGained(0), SHOW_MS)
    })
    const onVisible = () => {
      if (document.visibilityState === 'visible') nudgeStars(0)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      off()
      document.removeEventListener('visibilitychange', onVisible)
      if (timer.current) clearTimeout(timer.current)
    }
  }, [])

  return (
    <div className="starpulse" role="status" aria-live="polite">
      {joined && gained > 0 && (
        <>
          <span className="starpulse__pill" aria-hidden="true">
            <span className="starpulse__star">★</span> {t.pulse(gained)}
          </span>
          <span className="sr-only">{t.pulseSr(gained)}</span>
        </>
      )}
    </div>
  )
}
